import { makeModuleApiInfoSelector } from "@client/content/selectors";
import { apiAction } from "@client/lib/actions";
import { areEqual } from "@client/lib/array";
import { type BaseAppState, type EntityRef, Status } from "@client/types";
import { isNull } from "lodash";
import { type Dispatch } from "redux";
import { initAction } from "../actions";
import api from "./api";
import { findSection, getUrlState, mapToCourseAppState, pushUrlState } from "./lib";
import { localStateSelector, makeSectionsSelector } from "./selectors";
import { type CourseDetail } from "./types";

export enum Actions {
  InitBegin = "MEMBER_COURSE_DETAIL_INIT_BEGIN",
  InitFailure = "MEMBER_COURSE_DETAIL_INIT_FAILURE",
  InitSuccess = "MEMBER_COURSE_DETAIL_INIT_SUCCESS",

  GetLessonDetailBegin = "MEMBER_COURSE_DETAIL_GET_LESSON_DETAIL_BEGIN",
  GetLessonDetailFailure = "MEMBER_COURSE_DETAIL_GET_LESSON_DETAIL_FAILURE",
  GetLessonDetailSuccess = "MEMBER_COURSE_DETAIL_GET_LESSON_DETAIL_SUCCESS",

  LoadActiveCourseBegin = "MEMBER_COURSE_DETAIL_LOAD_ACTIVE_COURSE_BEGIN",
  LoadActiveCourseFailure = "MEMBER_COURSE_DETAIL_LOAD_ACTIVE_COURSE_FAILURE",
  LoadActiveCourseSuccess = "MEMBER_COURSE_DETAIL_LOAD_ACTIVE_COURSE_SUCCESS",

  LessonEventBegin = "MEMBER_COURSE_DETAIL_LESSON_EVENT_BEGIN",
  LessonEventFailure = "MEMBER_COURSE_DETAIL_LESSON_EVENT_FAILURE",
  LessonEventSuccess = "MEMBER_COURSE_DETAIL_LESSON_EVENT_SUCCESS",

  QuizGoTo = "MEMBER_COURSE_DETAIL_QUIZ_BACK",

  QuizProceedBegin = "MEMBER_COURSE_DETAIL_QUIZ_PROCEED_BEGIN",
  QuizProceedFailure = "MEMBER_COURSE_DETAIL_QUIZ_PROCEED_FAILURE",
  QuizProceedSuccess = "MEMBER_COURSE_DETAIL_QUIZ_PROCEED_SUCCESS",

  QuizReviewBegin = "MEMBER_COURSE_DETAIL_QUIZ_REVIEW_BEGIN",
  QuizReviewFailure = "MEMBER_COURSE_DETAIL_QUIZ_REVIEW_FAILURE",
  QuizReviewSuccess = "MEMBER_COURSE_DETAIL_QUIZ_REVIEW_SUCCESS",

  QuizReviewEnd = "MEMBER_COURSE_DETAIL_QUIZ_REVIEW_END",

  SetActiveCourseId = "MEMBER_COURSE_DETAIL_SET_ACTIVE_COURSE_ID",
  SetActiveLessonId = "MEMBER_COURSE_DETAIL_SET_ACTIVE_ITEM_ID",
  SetActiveSectionId = "MEMBER_COURSE_DETAIL_SET_ACTIVE_SECTION_ID"
}

export type InitBeginActionData = EntityRef & {
  courseId?: string;
};
export type InitSuccessActionData = {
  activeCourse: CourseDetail.CourseAppState | null;
  activeLesson: CourseDetail.ClientLesson | null;
};
export const _init = initAction<
  BaseAppState,
  InitBeginActionData,
  CourseDetail.Api.InitResponse,
  InitSuccessActionData
>({
  types: {
    begin: Actions.InitBegin,
    failure: Actions.InitFailure,
    success: [
      Actions.InitSuccess,
      ({ dispatch, getState, params, res }) => {
        const state = getState();
        // on a membership site we want the accordion on the page to be correctly auto-expanded on page reload
        if (!state.portal.programId) {
          const urlState = getUrlState();

          if (urlState.csid && urlState.cscid) {
            const localState = localStateSelector(getState(), params);
            if (localState.activeCourse) {
              const lesson = localState.activeCourse.lessons.find((x) => x.id === urlState.cscid);
              if (lesson && lesson.moduleId === urlState.csid && !lesson.isLocked) {
                dispatch(setActiveLessonId({ id: params.id, source: params.source, lessonId: urlState.cscid }));
                return;
              }
            }
          }

          pushUrlState({
            ...urlState,
            cid: undefined,
            csid: undefined,
            cscid: undefined
          });
        }
      }
    ]
  },
  action: (state: BaseAppState, dispatch: any, params: InitBeginActionData) => {
    const apiInfo = makeModuleApiInfoSelector()(state, params);
    return api.init(apiInfo, { portalId: state.portal.id, id: params.courseId });
  },
  mapResponse: (state: BaseAppState, resp: CourseDetail.Api.InitResponse): InitSuccessActionData => ({
    activeCourse: mapToCourseAppState(resp.activeCourse, !!state.portal.programId),
    activeLesson: resp.activeCourse?.activeLesson ?? null
  })
});

export type InitArgs = EntityRef & {};
export const init = (args: InitArgs) => (dispatch: Dispatch<any>, getState: () => BaseAppState) => {
  const state = getState();
  const localState = localStateSelector(state, args);
  if (localState.status === Status.None) {
    const urlState = getUrlState();
    dispatch(_init({ ...args, courseId: urlState.cid }));
  }
};

export type GetLessonDetailBeginActionData = EntityRef & {
  sectionId: string;
  lessonId: string;
};
export type GetLessonDetailSuccessActionData = CourseDetail.ClientLesson;
export const _getLessonDetail = apiAction<
  BaseAppState,
  GetLessonDetailBeginActionData,
  CourseDetail.Api.GetLessonDetailResponse,
  GetLessonDetailSuccessActionData
>({
  types: {
    begin: Actions.GetLessonDetailBegin,
    failure: Actions.GetLessonDetailFailure,
    success: Actions.GetLessonDetailSuccess
  },
  action: (state: BaseAppState, dispatch: any, params: GetLessonDetailBeginActionData) => {
    const apiInfo = makeModuleApiInfoSelector()(state, params);
    return api.getLessonDetail(apiInfo, params.id, params.lessonId);
  }
});

export type LoadActiveCourseBeginActionData = EntityRef & {
  courseId: string;
};
export type LoadActiveCourseSuccessActionData = CourseDetail.CourseAppState;
export const _loadActiveCourse = apiAction<
  BaseAppState,
  LoadActiveCourseBeginActionData,
  CourseDetail.Api.GetCourseResponse,
  LoadActiveCourseSuccessActionData
>({
  types: {
    begin: Actions.LoadActiveCourseBegin,
    failure: Actions.LoadActiveCourseFailure,
    success: [
      Actions.LoadActiveCourseSuccess,
      ({ dispatch, params, res }) => {
        if (res.activeLessonId) {
          dispatch(setActiveSectionId({ id: params.id, source: params.source, sectionId: res.activeModuleId }));
          dispatch(setActiveLessonId({ id: params.id, source: params.source, lessonId: res.activeLessonId }));
        }
      }
    ]
  },
  action: (state: BaseAppState, dispatch: any, params: LoadActiveCourseBeginActionData) => {
    const apiInfo = makeModuleApiInfoSelector()(state, params);
    return api.getCourse(apiInfo, { id: params.courseId });
  },
  mapResponse: (state: BaseAppState, resp: CourseDetail.Api.GetCourseResponse): LoadActiveCourseSuccessActionData =>
    mapToCourseAppState(resp, !!state.portal.programId)!
});

export const closeCourseModal = (ref: EntityRef) => (dispatch: Dispatch<any>, getState: () => BaseAppState) => {
  const state = getState();

  // on a membership site we want the accordion on the page to be correctly auto-expanded on page reload
  if (!state.portal.programId) {
    const urlState = getUrlState();

    pushUrlState({
      ...urlState,
      cid: undefined,
      csid: undefined,
      cscid: undefined
    });
  }

  dispatch(setActiveCourseId({ ...ref, courseId: null }));
};

export type QuizGoToActionData = EntityRef & {
  lessonId: string;
  questionId: string;
};
const quizGoTo = (data: QuizGoToActionData) => ({
  type: Actions.QuizGoTo,
  data: data
});

export type QuizBackActionData = EntityRef & {
  lessonId: string;
};
export const quizBack = (data: QuizBackActionData) => (dispatch: Dispatch<any>, getState: () => BaseAppState) => {
  const localState = localStateSelector(getState(), data);

  const quizState = localState.lessonDetail[data.lessonId].quizState!;
  const index = quizState.seenQuestions.findIndex((x) => quizState.question?.id === x.id);
  dispatch(
    quizGoTo({
      ...data,
      questionId: quizState.seenQuestions[index - 1].id
    })
  );
};

export type QuizProceedBeginActionData = EntityRef & {
  lessonId: string;
  questionId?: string;
  answerIds?: string[];
};
export type QuizProceedSuccessActionData = CourseDetail.Api.QuizProceedResponse;
const _quizProceed = apiAction<BaseAppState, QuizProceedBeginActionData, QuizProceedSuccessActionData>({
  types: {
    begin: Actions.QuizProceedBegin,
    failure: Actions.QuizProceedFailure,
    success: [
      Actions.QuizProceedSuccess,
      ({ dispatch, params, getState, res }) => {
        if (res.finishedOnDate) {
          const localState = localStateSelector(getState(), params);
          dispatch(leaveLesson({ id: params.id, source: params.source }, localState.activeCourseId!, params.lessonId)); // updates availability
        }
      }
    ]
  },
  action: (state: BaseAppState, dispatch: any, params: QuizProceedBeginActionData) => {
    const apiInfo = makeModuleApiInfoSelector()(state, params);
    const localState = localStateSelector(state, params);
    return api.quizProceed(apiInfo, localState.activeCourseId!, params.lessonId, params.questionId, params.answerIds);
  }
});

export const quizProceed =
  (data: QuizProceedBeginActionData) => (dispatch: Dispatch<any>, getState: () => BaseAppState) => {
    const localState = localStateSelector(getState(), data);

    if (data.answerIds) {
      const quizState = localState.lessonDetail[data.lessonId].quizState!;
      const index = quizState.seenQuestions.findIndex((x) => quizState.question?.id === x.id);
      if (
        index >= 0 &&
        index < quizState.seenQuestions.length - 1 &&
        areEqual(data.answerIds, quizState.seenQuestions[index].answerIds)
      ) {
        dispatch(
          quizGoTo({
            id: data.id,
            source: data.source,
            lessonId: data.lessonId,
            questionId: quizState.seenQuestions[index + 1].id
          })
        );
        return;
      }
    }

    dispatch(_quizProceed(data));
  };

export type QuizReviewBeginActionData = EntityRef & {
  lessonId: string;
};
export type QuizReviewSuccessActionData = CourseDetail.Api.QuizReviewResponse;
export const quizReview = apiAction<BaseAppState, QuizReviewBeginActionData, QuizReviewSuccessActionData>({
  types: {
    begin: Actions.QuizReviewBegin,
    failure: Actions.QuizReviewFailure,
    success: Actions.QuizReviewSuccess
  },
  action: (state: BaseAppState, dispatch: any, params: QuizReviewBeginActionData) => {
    const apiInfo = makeModuleApiInfoSelector()(state, params);
    const localState = localStateSelector(state, params);
    return api.quizReview(apiInfo, localState.activeCourseId!, params.lessonId);
  }
});

export const setActiveCourse =
  (ref: EntityRef, courseId: string) => (dispatch: Dispatch<any>, getState: () => BaseAppState) => {
    const urlState = getUrlState();

    pushUrlState({
      ...urlState,
      cid: courseId ?? undefined,
      csid: undefined,
      cscid: undefined
    });

    dispatch(setActiveCourseId({ ...ref, courseId }));

    const state = getState();
    const localState = localStateSelector(state, ref);

    if (!localState.activeCourse || localState.activeCourse.course.id !== courseId) {
      dispatch(_loadActiveCourse({ ...ref, courseId }));
    } else {
      dispatch(setActiveSectionId({ ...ref, sectionId: localState.activeCourse.activeModuleId }));
      dispatch(setActiveLessonId({ ...ref, lessonId: localState.activeCourse.activeLessonId }));
    }
  };

export type SetActiveCourseIdActionData = EntityRef & {
  courseId: string | null;
};
const setActiveCourseId = (data: SetActiveCourseIdActionData) => ({
  type: Actions.SetActiveCourseId,
  data
});

export type SetActiveLessonIdActionData = EntityRef & {
  lessonId: string | null;
};
export const setActiveLessonId =
  (args: SetActiveLessonIdActionData) => (dispatch: Dispatch<any>, getState: () => BaseAppState) => {
    const localState = localStateSelector(getState(), args);

    if (args.lessonId) {
      if (!localState.activeCourseId) {
        dispatch(setActiveCourseId({ id: args.id, source: args.source, courseId: localState.activeCourse!.course.id }));
      }

      const section = findSection(localState.activeCourse!, args.lessonId);
      if (!(args.lessonId in localState.lessonDetail)) {
        dispatch(
          _getLessonDetail({ id: args.id, source: args.source, sectionId: section.id, lessonId: args.lessonId })
        );
      }

      dispatch(setActiveSectionId({ id: args.id, source: args.source, sectionId: section.id }));

      const urlState = getUrlState();
      pushUrlState({
        ...urlState,
        csid: section.id,
        cscid: args.lessonId
      });
    } else {
      const urlState = getUrlState();
      pushUrlState({
        ...urlState,
        cscid: undefined
      });
    }

    dispatch({
      type: Actions.SetActiveLessonId,
      data: <SetActiveLessonIdActionData>args
    });
  };

export const setActiveSectionIndex =
  (ref: EntityRef, index: number | null) => (dispatch: Dispatch<any>, getState: () => BaseAppState) => {
    const sections = makeSectionsSelector()(getState(), ref);
    const section = isNull(index) ? null : sections[index];

    dispatch(setActiveSectionId({ ...ref, sectionId: section?.id ?? null }));
  };

export type SetActiveSectionIdActionData = EntityRef & {
  sectionId: string | null;
};
export const setActiveSectionId =
  (args: SetActiveSectionIdActionData) => (dispatch: Dispatch<any>, getState: () => BaseAppState) => {
    const urlState = getUrlState();
    pushUrlState({
      ...urlState,
      csid: args.sectionId ?? undefined
    });

    dispatch({
      type: Actions.SetActiveSectionId,
      data: args
    });
  };

export const startQuiz =
  (ref: EntityRef, lessonId: string) => (dispatch: Dispatch<any>, getState: () => BaseAppState) =>
    dispatch(quizProceed({ ...ref, lessonId }));

export type QuizReviewEndActionData = EntityRef & {
  lessonId: string;
};
export const quizReviewEnd = (ref: EntityRef, lessonId: string) => ({
  type: Actions.QuizReviewEnd,
  data: <QuizReviewEndActionData>{ ...ref, lessonId }
});

export type LessonEventBeginActionData = EntityRef & {
  courseId: string;
  lessonId: string;
  event: CourseDetail.LessonEventType;
};
export type LessonEventSuccessActionData = CourseDetail.Api.LessonEventResponse;
const lessonEvent = apiAction<BaseAppState, LessonEventBeginActionData, LessonEventSuccessActionData>({
  types: {
    begin: Actions.LessonEventBegin,
    failure: Actions.LessonEventFailure,
    success: Actions.LessonEventSuccess
  },
  action: (state: BaseAppState, dispatch: any, params: LessonEventBeginActionData) => {
    const apiInfo = makeModuleApiInfoSelector()(state, params);
    return api.lessonEvent(apiInfo, params.courseId, params.lessonId, params.event);
  }
});

export const enterLesson =
  (ref: EntityRef, courseId: string, lessonId: string) => (dispatch: Dispatch<any>, getState: () => BaseAppState) => {
    dispatch(lessonEvent({ ...ref, courseId, lessonId, event: "enter" }));
  };

export const leaveLesson =
  (ref: EntityRef, courseId: string, lessonId: string) => (dispatch: Dispatch<any>, getState: () => BaseAppState) => {
    dispatch(lessonEvent({ ...ref, courseId, lessonId, event: "leave" }));
  };
